import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { CheckboxField } from '@wk/components-v3-react16';
import { useStatusConfig } from '../../useStatusConfig';
import { useStatusFilter } from './useStatusFilter';
import type { StatusFilterModel } from './types';
import styles from './StatusFilter.module.scss';

interface StatusFilterProperties
  extends CustomFilterDisplayProps<StatusFilterModel>,
    FilterWrapperParams {}

const StatusFilter = ({
  model,
  onStateChange,
  state,
}: StatusFilterProperties) => {
  const containerReference = useRef<HTMLDivElement>(null);
  const selectAllInputReference = useRef<HTMLInputElement>(null);
  const { statusConfig } = useStatusConfig();

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters?.suppressFocus) {
        containerReference.current?.focus();
      }
    },
    []
  );

  useGridFilterDisplay({ afterGuiAttached });
  useStatusFilter(model);

  const statusOptions = useMemo(() => statusConfig.slice(1), [statusConfig]);

  const allStatusCodes = useMemo(
    () => statusOptions.map(({ status }) => status),
    [statusOptions]
  );

  // No applied model means the filter is untouched or was just reset -
  // both cases default to every status being selected.
  const selectedStatuses: string[] = useMemo(
    () => state.model?.jobStatuses ?? allStatusCodes,
    [state.model?.jobStatuses, allStatusCodes]
  );

  const isAllSelected = useMemo(
    () =>
      statusOptions.length > 0 &&
      statusOptions.every(({ status }) => selectedStatuses.includes(status)),
    [statusOptions, selectedStatuses]
  );

  const isIndeterminate = useMemo(
    () =>
      !isAllSelected &&
      statusOptions.some(({ status }) => selectedStatuses.includes(status)),
    [isAllSelected, statusOptions, selectedStatuses]
  );

  useEffect(() => {
    if (selectAllInputReference.current) {
      selectAllInputReference.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const updateModel = useCallback(
    (updatedStatuses: string[]) => {
      // Every status selected is equivalent to no filter being applied, so
      // it collapses to a `null` model - matching the default state. An
      // empty selection must stay an explicit empty list (not `null`),
      // otherwise it would fall back to "all selected" and Select All could
      // never be unchecked.
      const isFullSelection = updatedStatuses.length === allStatusCodes.length;
      const newModel = isFullSelection
        ? null
        : { jobStatuses: updatedStatuses };
      onStateChange({ model: newModel });
    },
    [onStateChange, allStatusCodes]
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const status = event.target.value;
      const isSelected = selectedStatuses.includes(status);
      const updatedStatuses = isSelected
        ? selectedStatuses.filter((selectedStatus) => selectedStatus !== status)
        : [...selectedStatuses, status];
      updateModel(updatedStatuses);
    },
    [selectedStatuses, updateModel]
  );

  const handleSelectAll = useCallback(() => {
    updateModel(isAllSelected ? [] : allStatusCodes);
  }, [isAllSelected, allStatusCodes, updateModel]);

  return (
    <div className={styles.container} ref={containerReference} tabIndex={-1}>
      <div className={styles.selectAllWrapper}>
        <CheckboxField
          labelFor="select-all-statuses"
          label="Select All"
          size="extra-small"
        >
          <input
            ref={selectAllInputReference}
            id="select-all-statuses"
            name="SelectAll"
            value="select-all"
            type="checkbox"
            checked={isAllSelected}
            aria-checked={isIndeterminate ? 'mixed' : isAllSelected}
            onChange={handleSelectAll}
            flow-id="checkbox"
          />
        </CheckboxField>
      </div>

      <div className={styles.checkboxList}>
        {statusOptions.map(({ status, label }) => {
          const inputId = `status-${status}`;
          return (
            <div key={status} className={styles.checkboxWrapper}>
              <CheckboxField labelFor={inputId} label={label} size="extra-small">
                <input
                  id={inputId}
                  name={status}
                  value={status}
                  type="checkbox"
                  checked={selectedStatuses.includes(status)}
                  onChange={handleCheckboxChange}
                  flow-id="checkbox"
                />
              </CheckboxField>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StatusFilter;
